<template>
  <div class="modal-overlay blurred" ref="refRoot" tabindex="0" @mousedown="onMouseDown" @mouseup="onMouseUp">
    <div class="modal-content">
      <header v-if="item.title || item.showCloseButton">
        <h3>{{ item.title }}</h3>

        <div v-if="item.showCloseButton" class="close-icon">
          <CloseIcon @click="handleClose" />
        </div>
      </header>

      <main>
        <div class="txt-wrap" v-if="item.subTitle">
          <p class="sub-txt">{{ item.subTitle }}</p>
        </div>

        <template v-if="item.contentSlot">
          <component :is="item.contentSlot" :payload="item.payload" />
        </template>

        <div class="modal-footer" v-if="item.cancelButton?.text || item.confirmButton?.text">
          <button class="l-btn gray-btn" v-if="item.cancelButton?.text" @click="popupStore.cancel(item.id)">
            {{ item.cancelButton.text }}
          </button>

          <button
            class="l-btn primary-btn"
            v-if="item.confirmButton?.text"
            :disabled="isLoading"
            @click="handleConfirm"
          >
            {{ item.confirmButton.text }}
          </button>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import CloseIcon from '@/components/icons/IconClose.vue';
import { usePopupStore } from '@/stores/popup';
import { PopupItem } from '@/types/components';

const props = defineProps<{ item: PopupItem }>();

const popupStore = usePopupStore();

const isLoading = ref<boolean>(false);
const refRoot = ref<HTMLElement | null>(null);

let mouseDownTarget: EventTarget | null = null;

const onMouseDown = (e: MouseEvent) => {
  mouseDownTarget = e.target;
};

const onMouseUp = (e: MouseEvent) => {
  if (e.target === e.currentTarget && mouseDownTarget === e.currentTarget) {
    handleClose();
  }
};

const handleClose = () => {
  popupStore.close(props.item.id);
};

const handleConfirm = () => {
  if (isLoading.value) {
    return;
  }

  isLoading.value = true;

  popupStore.confirm(props.item.id);

  isLoading.value = false;
};

onMounted(() => {
  refRoot.value?.focus();
  document.body.style.overflow = 'hidden';
});

onUnmounted(() => {
  document.body.style.overflow = '';
});
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1500;
}

.modal-overlay.blurred {
  background: var(--surface-glass-dark);
  backdrop-filter: $blur-medium;
}

.modal-content {
  width: 100%;
  max-width: 480px;
  height: fit-content;
  border-radius: 12px;
  background: var(--surface-overlay);
  box-shadow: $shadow-modal;
  position: relative;
}

main {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 1.25rem 1.25rem 1.25rem;
  overflow-x: hidden;
  .txt-wrap {
    text-align: center;

    .sub-txt {
      font-size: $font-size-p;
      color: $color-gray-500;
      margin-top: $size-xs;
      white-space: pre-wrap;
    }
  }
}

header {
  display: flex;
  justify-content: space-between;
  padding: 1.25rem;
  h3 {
    color: $color-gray-700;
    font-size: $font-size-h6;
  }
  .close-icon {
    display: flex;
    cursor: pointer;
    width: 1.5rem;
    height: 1.5rem;
    svg {
      color: $color-gray-700;
    }
  }
}

.move-up {
  transition: transform 0.3s ease-in-out;
}

.modal-footer {
  display: flex;
  padding-top: 1rem;
  gap: $size-s;
  button {
    flex: 1;
  }
}
</style>
